import type { CommerceProduct } from '@/config/shared/commerce';

export type ProductCatalogOptions = {
  readonly defaultCurrency?: string;
};

export type ProductCatalogSnapshot = {
  readonly products: CommerceProduct[];
  readonly count: number;
};

/**
 * Catálogo de produtos configurados em `config.commerce.catalog`.
 * Isolado por instância — sem estado global / module-level.
 * Não acessa DOM, Transport nem altera payloads de eventos.
 */
export class ProductCatalog {
  private readonly productsById = new Map<string, CommerceProduct>();

  private readonly productsBySku = new Map<string, CommerceProduct>();

  private readonly orderedIds: string[] = [];

  constructor(
    products: readonly CommerceProduct[] | undefined = [],
    options: ProductCatalogOptions = {}
  ) {
    this.indexProducts(products ?? [], options.defaultCurrency);
  }

  list(): CommerceProduct[] {
    return this.orderedIds.map(id =>
      this.cloneProduct(this.productsById.get(id)!)
    );
  }

  getById(id: string): CommerceProduct | null {
    if (!id) return null;
    const product = this.productsById.get(id);
    return product ? this.cloneProduct(product) : null;
  }

  getBySku(sku: string): CommerceProduct | null {
    if (!sku) return null;
    const product = this.productsBySku.get(sku);
    return product ? this.cloneProduct(product) : null;
  }

  hasProduct(id: string): boolean {
    return Boolean(id) && this.productsById.has(id);
  }

  getSnapshot(): ProductCatalogSnapshot {
    const products = this.list();
    return { products, count: products.length };
  }

  private indexProducts(
    products: readonly CommerceProduct[],
    defaultCurrency?: string
  ): void {
    for (const raw of products) {
      if (!raw || typeof raw !== 'object') continue;

      const id = typeof raw.id === 'string' ? raw.id.trim() : '';
      if (!id) continue;

      // Duplicado por id: mantém o primeiro (determinístico).
      if (this.productsById.has(id)) continue;

      const product = this.normalizeProduct(raw, id, defaultCurrency);
      this.productsById.set(id, product);
      this.orderedIds.push(id);

      const sku = typeof product.sku === 'string' ? product.sku.trim() : '';
      if (sku && !this.productsBySku.has(sku)) {
        this.productsBySku.set(sku, product);
      }
    }
  }

  private normalizeProduct(
    raw: CommerceProduct,
    id: string,
    defaultCurrency?: string
  ): CommerceProduct {
    const sku =
      typeof raw.sku === 'string' && raw.sku.trim()
        ? raw.sku.trim()
        : undefined;

    const currency =
      typeof raw.currency === 'string' && raw.currency.trim()
        ? raw.currency.trim()
        : defaultCurrency;

    const quantity =
      typeof raw.quantity === 'number' && Number.isFinite(raw.quantity)
        ? raw.quantity
        : undefined;

    return {
      ...raw,
      id,
      ...(sku !== undefined ? { sku } : {}),
      ...(currency !== undefined ? { currency } : {}),
      ...(quantity !== undefined ? { quantity } : {}),
      ...(raw.metadata ? { metadata: { ...raw.metadata } } : {}),
    };
  }

  private cloneProduct(product: CommerceProduct): CommerceProduct {
    return {
      ...product,
      ...(product.metadata ? { metadata: { ...product.metadata } } : {}),
    };
  }
}
